import React from "react";
import { CalendarDays, MapPin, Building2, ChevronRight } from "lucide-react";
import type { Event } from "../types";
import { useTranslation } from "react-i18next";
import { formatDateRange } from "../i18n";
import { EventCoverImage } from "./EventCoverImage";
import { WhiteLogoPlate } from "./WhiteLogoPlate";

function StatusBadge({ status }: { status: Event["status"] }) {
  const { t } = useTranslation();
  const map = {
    active: { label: t("home.status_active"), cls: "bg-emerald-100 text-emerald-700" },
    upcoming: { label: t("home.status_upcoming"), cls: "bg-indigo-100 text-indigo-700" },
    past: { label: t("home.status_past"), cls: "bg-gray-100 text-gray-500" },
  };
  const { label, cls } = map[status];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] ${cls}`}>
      {status === "active" && (
        <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-1 animate-pulse" />
      )}
      {label}
    </span>
  );
}

export function EventPosterCard({
  event,
  onSelect,
  current,
}: {
  event: Event;
  onSelect: (event: Event) => void;
  current?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const place = [event.venueName, event.city].filter(Boolean).join(", ");
  // Prefer logo over hero so list cards share a consistent logo band (IDEA-013).
  const imageSrc = (event.logoUrl || event.heroImage || "").trim();

  return (
    <button
      type="button"
      onClick={() => onSelect(event)}
      className={`w-full text-left rounded-2xl overflow-hidden bg-white border transition-transform active:scale-[0.98] ${
        current
          ? "border-indigo-200 shadow-[0_8px_28px_rgba(99,102,241,0.18)]"
          : "border-gray-100 shadow-[0_4px_20px_rgba(0,0,0,0.06)]"
      }`}
    >
      <WhiteLogoPlate className="w-full h-36 border-b border-gray-50">
        {imageSrc ? (
          <div className="flex items-center justify-center h-full w-full py-3 px-5">
            <EventCoverImage
              src={imageSrc}
              alt={event.title}
              imgClassName="block max-w-full w-auto h-auto max-h-[7.5rem] object-contain object-center"
            />
          </div>
        ) : (
          <div className="w-full h-full min-h-28 flex items-center justify-center text-slate-400 text-[13px] px-4 text-center">
            {event.title}
          </div>
        )}
        <div className="absolute top-3 left-3 flex items-center gap-1.5 z-10">
          <StatusBadge status={event.status} />
          {current && (
            <span className="text-[10px] text-white bg-indigo-600/90 px-1.5 py-0.5 rounded-full">
              {t("visitor.current")}
            </span>
          )}
        </div>
      </WhiteLogoPlate>

      <div className="p-3.5 space-y-2">
        <h3 className="text-[16px] text-gray-900 leading-snug line-clamp-2">{event.title}</h3>
        {event.description?.trim() && (
          <p className="text-[13px] text-gray-500 leading-snug line-clamp-4">{event.description}</p>
        )}
        <div className="space-y-1 pt-0.5">
          <p className="text-[12px] text-gray-500 flex items-center gap-1.5">
            <CalendarDays size={13} className="text-indigo-400 flex-shrink-0" />
            {formatDateRange(event.startDate, event.endDate, i18n.language)}
          </p>
          {place && (
            <p className="text-[12px] text-gray-400 flex items-center gap-1.5">
              <MapPin size={13} className="text-indigo-400 flex-shrink-0" />
              <span className="line-clamp-2">{place}</span>
            </p>
          )}
        </div>
        <div className="flex items-center justify-between pt-1 border-t border-gray-50">
          <span className="text-[12px] text-gray-400 flex items-center gap-1">
            <Building2 size={13} />
            {t("home.companies_count", { n: event.companiesCount })}
          </span>
          <ChevronRight size={16} className="text-indigo-400" />
        </div>
      </div>
    </button>
  );
}
